import React, { useState, useEffect } from 'react';
import { Box, Card, Typography, TextField, Button, CircularProgress } from '@mui/material';
import { useGetItemsQuery, useGetLibraryItemsQuery } from '../store/docApi';
import ColorChartThumbnail from '../components/charts/ColorChartThumbnail';

const MotifBrowser = ({ onSelectMotif, onClose }) => {
  const { data: motifs, isLoading: isLoadingMotifs } = useGetItemsQuery({ label: 'motif' });
  const { data: workspaces, isLoading: isLoadingWorkspaces } = useGetItemsQuery({ label: 'workspace' });
  const { data: libraryWorkspaces } = useGetLibraryItemsQuery({ label: 'workspace' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLibraryWorkspaces, setFilteredLibraryWorkspaces] = useState([]);
  const [filteredWorkspaces, setFilteredWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);

  // Helper function to convert motifs object to array if needed
  const convertMotifsToArray = (motifs) => {
    if (!motifs) return [];
    if (Array.isArray(motifs)) return motifs;
    return Object.entries(motifs).map(([key, motif]) => ({ ...motif, id: key }));
  };

  useEffect(() => {
    if (motifs) {
      // motifs is individual motifs, not workspaces
      // Don't set it to filteredLibraryWorkspaces
    }
    if (workspaces) {
      setFilteredWorkspaces(workspaces);
    }
    if (libraryWorkspaces) {
      setFilteredLibraryWorkspaces(libraryWorkspaces);
    }
  }, [motifs, workspaces, libraryWorkspaces]);

  useEffect(() => {
    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      setFilteredLibraryWorkspaces(
        libraryWorkspaces?.filter(
          (workspace) =>
            (workspace.name && workspace.name.toLowerCase().includes(lowercasedTerm)) ||
            (workspace.description && workspace.description.toLowerCase().includes(lowercasedTerm))
        ) || []
      );
      setFilteredWorkspaces(
        workspaces?.filter(
          (workspace) =>
            (workspace.name && workspace.name.toLowerCase().includes(lowercasedTerm)) ||
            (workspace.description && workspace.description.toLowerCase().includes(lowercasedTerm))
        ) || []
      );
    } else {
      setFilteredLibraryWorkspaces(libraryWorkspaces || []);
      setFilteredWorkspaces(workspaces || []);
    }
  }, [searchTerm, libraryWorkspaces, workspaces]);

  const handleSelectWorkspace = (workspace) => {
    setSelectedWorkspace(workspace);
  };

  const handleBackToWorkspaces = () => {
    setSelectedWorkspace(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TextField
          placeholder="Search motifs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          fullWidth
          sx={{ mr: 2 }}
        />
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </Box>

      <Box>
        {isLoadingMotifs || isLoadingWorkspaces ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {selectedWorkspace ? (
              <Box>
                <Button variant="outlined" onClick={handleBackToWorkspaces} sx={{ mb: 2 }}>
                  Back to Workspaces
                </Button>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}
                >
                  {convertMotifsToArray(selectedWorkspace.motifs)?.map((motif, index) => (
                    <Card
                      key={motif.id || index}
                      variant="outlined"
                      onClick={() => onSelectMotif(motif)}
                      sx={{ p: 2, cursor: 'pointer', '&:hover': { boxShadow: 1 } }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {motif.name}
                      </Typography>
                      <ColorChartThumbnail data={motif.data} colors={motif.colors} settings={motif.settings} />
                      <Typography variant="body2" color="text.secondary">
                        {motif.description}
                      </Typography>
                    </Card>
                  ))}
                </Box>
              </Box>
            ) : (
              <Box>
                {/* Motifs */}
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Motifs
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}
                >
                  {convertMotifsToArray(motifs)?.map((motif, index) => (
                    <Card
                      key={motif.id || index}
                      variant="outlined"
                      onClick={() => onSelectMotif(motif)}
                      sx={{ p: 2, cursor: 'pointer', '&:hover': { boxShadow: 1 } }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {motif.name}
                      </Typography>
                      <ColorChartThumbnail data={motif.data} colors={motif.colors} settings={motif.settings} />
                      <Typography variant="body2" color="text.secondary">
                        {motif.description}
                      </Typography>
                    </Card>
                  ))}
                </Box>

                {/* Workspaces */}
                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                  Workspaces
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}
                >
                  {filteredWorkspaces.map((workspace, index) => {
                    const motifCount = workspace.motifs ? (Array.isArray(workspace.motifs) ? workspace.motifs.length : Object.keys(workspace.motifs).length) : 0;
                    
                    return (
                      <Card
                        key={index}
                        variant="outlined"
                        onClick={() => handleSelectWorkspace(workspace)}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          '&:hover': { boxShadow: 1 },
                          maxWidth: 200
                        }}
                      >
                        <Typography variant="body2" textAlign="center">
                          {workspace.name}
                          {motifCount > 0 && ` (${motifCount})`}
                        </Typography>
                        {workspace.picture && <img src={workspace.picture} alt={workspace.name} style={{ maxHeight: 100, maxWidth: 100 }} />}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            WebkitLineClamp: 2
                          }}
                        >
                          {workspace.description}
                        </Typography>
                      </Card>
                    );
                  })}
                </Box>

                {/* Library Workspaces */}
                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                  Library
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}
                >
                  {filteredLibraryWorkspaces.map((workspace, index) => {
                    const motifCount = workspace.motifs ? (Array.isArray(workspace.motifs) ? workspace.motifs.length : Object.keys(workspace.motifs).length) : 0;
                    
                    return (
                      <Card
                        key={index}
                        variant="outlined"
                        onClick={() => handleSelectWorkspace(workspace)}
                        sx={{ p: 2, cursor: 'pointer', '&:hover': { boxShadow: 1 } }}
                      >
                        <Typography variant="body2" textAlign="center">
                          {workspace.name}
                          {motifCount > 0 && ` (${motifCount})`}
                        </Typography>
                        {workspace.picture && <img src={workspace.picture} alt={workspace.name} style={{ maxHeight: 100, maxWidth: 100 }} />}
                        <Typography variant="body2" color="text.secondary">
                          {workspace.description}
                        </Typography>
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MotifBrowser;
